import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import {
    collapsePurchaseOrderPartsByPartId,
    PurchaseOrdersFeedInput,
    PurchaseOrdersFeedResponse,
} from '../../types/inventory/purchaseOrders';

const getPurchaseOrdersFeed = async (
    sessionId: string,
    input: PurchaseOrdersFeedInput,
): Promise<PurchaseOrdersFeedResponse> => {
    const { data } = await axios.post<PurchaseOrdersFeedResponse>(
        `${API_URL}/inventory/purchase-orders/feed`,
        input,
        { headers: { Authorization: sessionId } },
    );
    return {
        ...data,
        requests: (data.requests ?? []).map((request) => ({
            ...request,
            invoiceNumber: request.invoiceNumber ?? null,
            parts: collapsePurchaseOrderPartsByPartId(request.parts ?? []),
        })),
    };
};

export const useGetPurchaseOrdersFeed = (
    input: PurchaseOrdersFeedInput = {},
    options?: { enabled?: boolean },
) => {
    const sessionContext = useContext(SessionContext);

    // An empty selection would become `$in: []` on the server and match
    // nothing; treat it as 'all' (the server still clamps to the session's
    // authorized locations). Mirrors useInventoryFeed.
    const locationIds =
        Array.isArray(input.locationIds) && input.locationIds.length === 0
            ? 'all'
            : input.locationIds;
    const feedInput: PurchaseOrdersFeedInput = { ...input, locationIds };

    return useQuery({
        queryKey: [
            '/inventory/purchase-orders/feed',
            sessionContext?.organizationId ?? '',
            input.statusFilter ?? 'pending',
            // Omitted locationIds means the server scopes to the session's
            // authorized locations, which is not the same result as 'all'.
            locationIds === undefined
                ? 'session'
                : locationIds === 'all'
                    ? 'all'
                    : locationIds.join(','),
            input.search ?? '',
            input.purchaseOrderId ?? '',
            input.cursor ?? '',
            input.limit ?? 50,
        ],
        queryFn: () => getPurchaseOrdersFeed(sessionContext?.sessionId ?? '', feedInput),
        enabled: (options?.enabled ?? true) && !!sessionContext?.sessionId,
        staleTime: 0,
        refetchOnMount: 'always',
        refetchInterval: 1000 * 60 * 1,
    });
};
